import { useResourceKit } from '@resource-kit/core/react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../primitives/DropdownMenu';
import type { ActionDefinition } from '@resource-kit/controllers/react';
import { useRowActionRunner } from './useRowActionRunner';

interface RowActionsCellProps<T> {
  row: T;
  /** Stable business id for the row — the key into the shared busy registry. */
  rowId: string;
  actions: ActionDefinition<T>[];
  /** Resource slug — drives the query-key invalidation namespace. */
  slug: string;
}

/**
 * Renders the schema-declared `rowActions` for a single table row as a
 * dropdown menu (#1287, v2 pipeline in #1416). The execution pipeline —
 * permission gate, per-row eligibility, confirm (static OR custom surface),
 * result/error-branch feedback, the SHARED per-row busy registry, and query
 * invalidation — lives in `useRowActionRunner`, shared verbatim with the
 * detail-panel footer (#1417). This component is the dropdown presentation
 * over that runner.
 *
 * A row whose every action is filtered out (no grant / not eligible) renders
 * nothing — no empty trigger.
 */
export function RowActionsCell<T>({ row, rowId, actions, slug }: RowActionsCellProps<T>) {
  const { t } = useResourceKit();
  const { run, filterVisible, isBusy, confirmModal } = useRowActionRunner<T>({ row, rowId, slug });

  const visible = filterVisible(actions);
  if (visible.length === 0) return null;

  return (
    // Stop propagation so neither the trigger click NOR a menu-item click
    // (which bubbles through the React portal tree back up to this cell)
    // reaches the row's onClick that opens the detail sheet.
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger
          data-slot="row-actions-trigger"
          aria-label={t('rk.resource.rowActionsLabel')}
          aria-busy={isBusy}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-surface-2 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MoreHorizontal className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {visible.map((action) => (
            <DropdownMenuItem
              key={action.id}
              variant={action.intent === 'danger' ? 'destructive' : 'default'}
              onClick={() => {
                void run(action);
              }}
            >
              {t(action.label, { defaultValue: action.label })}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {confirmModal}
    </div>
  );
}
